import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useChangeFloodStageDeviceSettingsMutation } from 'services/devices/devices.api';

const getRejectedFields = (result) => {
  const body = result?.data ?? result;
  return Array.isArray(body?.rejected) ? body.rejected : [];
};

const useSettingsSave = ({ savePlan, setPendingDownstream }) => {
  const [changeFloodStageDeviceSettings] = useChangeFloodStageDeviceSettingsMutation();
  const [commandSent, setCommandSent] = useState(false);

  useEffect(() => {
    if (!commandSent) return;
    const timeout = setTimeout(() => setCommandSent(false), 5000);
    return () => clearTimeout(timeout);
  }, [commandSent]);

  const saveSettings = async () => {
    const { selectedDeviceSave, downstreamSaves } = savePlan;
    const savedNames = [];
    const warnings = [];
    if (selectedDeviceSave) {
      const { deviceId, deviceName, deviceSettings } = selectedDeviceSave;
      try {
        const result = await changeFloodStageDeviceSettings({ deviceId, deviceSettings }).unwrap();
        const rejected = getRejectedFields(result);
        if (rejected.length) {
          warnings.push(`${deviceName}: the server refused ${rejected.join('; ')}`);
        }
        savedNames.push(deviceName);
      } catch (error) {
        console.error(error?.message ?? error);
        toast.error(
          `${deviceName}: ${error?.data?.message ?? error?.message ?? 'the server refused the settings'}. Nothing else was written.`,
        );
        return false;
      }
    }

    // Await each request in staging order. A downstream failure must not prevent
    // later targets from saving, and only failed requests stay staged for retry.
    const stillPending = [];
    for (const { deviceId, deviceName, deviceSettings, pendingLink } of downstreamSaves) {
      try {
        const result = await changeFloodStageDeviceSettings({ deviceId, deviceSettings }).unwrap();
        const rejected = getRejectedFields(result);
        if (rejected.length) {
          warnings.push(`${deviceName}: the server refused ${rejected.join('; ')}`);
        }
        savedNames.push(deviceName);
      } catch (error) {
        console.error(error?.message ?? error);
        warnings.push(
          `${deviceName}: ${error?.data?.message ?? error?.message ?? 'the server refused the settings'} — the link stays staged`,
        );
        stillPending.push(pendingLink);
      }
    }
    setPendingDownstream(stillPending);

    if (savedNames.length) toast.success(`Saved: ${savedNames.join(', ')}`);
    for (const warning of warnings) toast.warn(warning, { autoClose: 12000 });
    if (savedNames.length && warnings.length === 0) setCommandSent(true);
    return warnings.length === 0;
  };

  return { saveSettings, commandSent };
};

export default useSettingsSave;
